///<amd-module name="Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/services/agentServices" />
import * as rest from "Core/Medius.Core.Web/Scripts/Medius/core/fetch/rest";
import * as remediumApi from "Core/Medius.Core.Web/Scripts/Medius/core/communication/remediumApi";
import { IMessage, IPredefinedQuestion, ICommentToSummarize } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/agentInterfaces";
import { EventSourceMessage } from "@microsoft/fetch-event-source";
import { CommentToTranslate } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/CommentsTooltip/CopilotComments";

export async function getMessages(){
    return new Promise<IMessage[]>(resolve => {
        setTimeout(() => {
            resolve([]);
        }, 100);
    });
}

export async function getPredefinedQuestions(documentId: number): Promise<IPredefinedQuestion[]> {
    const path = "remedium-api/ap-automation/agent/predefinedQuestions/" + documentId;
    const response = await remediumApi.get(path);
    return await response.json();
}

export async function getPredefinedQuestionsForPdfViewer(documentId: number): Promise<IPredefinedQuestion[]> {
    const path = "remedium-api/ap-automation/agent/predefinedQuestionsForPdfViewer/" + documentId;
    const response = await remediumApi.get(path);
    return await response.json();
}

export async function getPredefinedQuestionsNotFullyOperational(documentId: number): Promise<IPredefinedQuestion[]> {
    const path = "remedium-api/ap-automation/agent/predefinedQuestionsNotFullyOperational/" + documentId;
    const response = await remediumApi.get(path);
    return await response.json();
}

export async function getLegalPredefinedQuestions(): Promise<IPredefinedQuestion[]> {
    const path = "remedium-api/ap-automation/agent/legalPredefinedQuestions";
    const response = await remediumApi.get(path);
    return await response.json();
}

export async function getPredictedNextApprovers(invoiceId: number, invoiceType: string, workflowStepName: string): Promise<string[]> {
    const path = `remedium-api/ap-automation/agent/predictedNextAuthorizers?invoiceId=${invoiceId}&invoiceType=${invoiceType}&workflowStepName=${workflowStepName}`;
    const response = await remediumApi.get(path);
    return await response.json();
}

export async function streamResponse(
    documentId: number, 
    taskId: number,
    workflowStepName: string,
    invoiceType: string,
    temporaryDelegationMessage: string,
    escalationMessage: string,
    isOperational: boolean,
    prompt: string, 
    conversationId: string,
    conversationHistory: any[],
    handleChunk: (chunk: IMessage) => void,
    handleStreamEnd: () => void,
    signal: AbortSignal,
    timeout: NodeJS.Timeout,
    disablePredefinedQuestions: boolean,
    isFollowupQuestion: boolean,
    questionId?: number,
) {
    const data = {
        DocumentId: documentId,
        TaskId: taskId,
        IsOperational: isOperational,
        Prompt: prompt,
        ConversationId: conversationId,
        ConversationHistory: conversationHistory,
        QuestionId: questionId,
        WorkflowStepName: workflowStepName,
        InvoiceType: stripNamespace(invoiceType),
        TemporaryDelegationMessage: temporaryDelegationMessage,
        EscalationMessage: escalationMessage,
        DisablePredefinedQuestions: disablePredefinedQuestions,
        IsFollowupQuestion: isFollowupQuestion
    };

    const options = {
        onmessage: (event: EventSourceMessage) => {
            const jsonChunk: IMessage = JSON.parse(event.data);
            clearTimeout(timeout);
            handleChunk(jsonChunk);
        },
        onclose: () => {
            handleStreamEnd();
        },
        onerror: (error: Error) => {
            clearTimeout(timeout);
            handleStreamEnd();
            throw error;
        },
        signal: signal
    };

    const path = "remedium-api/ap-automation/agent/chat";
    await rest.eventStreamPost(path, data, options);
}

export async function sendCommentsSummaryRequestAndStreamResponse(
    documentId: number,
    comments: ICommentToSummarize[],
    handleChunk: (chunk: IMessage) => void,
    handleStreamEnd: () => void,
    signal: AbortSignal,
    timeout: NodeJS.Timeout,
) {
    const data = {
        DocumentId: documentId,
        Comments: comments
    };

    const options = {
        onmessage: (event: EventSourceMessage) => {
            const jsonChunk: IMessage = JSON.parse(event.data);
            clearTimeout(timeout);
            handleChunk(jsonChunk);
        },
        onclose: () => {
            handleStreamEnd();
        },
        onerror: (error: Error) => {
            clearTimeout(timeout);
            handleStreamEnd();
            throw error;
        },
        signal: signal
    };

    const path = "remedium-api/ap-automation/agent/commentsSummary";
    await rest.eventStreamPost(path, data, options);
}

export async function getCommentsTranslation(
    documentId: number,
    comments: CommentToTranslate[],
) {
    const data = {
        DocumentId: documentId,
        Comments: comments
    };

    const path = "remedium-api/ap-automation/agent/commentsTranslation";
    
    const response = await rest.post(path, data);

    return response;
}

export async function sendFeedback(
    conversationId: string, 
    documentId: number,
    feedback: string,
    workflowStepName : string,
    invoiceType: string
) {
    const data = {
        ConversationId: conversationId,
        DocumentId: documentId,
        Feedback: feedback,
        WorkflowStepName: workflowStepName,
        InvoiceType: stripNamespace(invoiceType)
    };

    const path = "remedium-api/ap-automation/agent/feedback";
    await rest.post(path, data);
}

export async function getFollowupQuestions(
    freetextQuestion: string,
    copilotResponse: string,
    isOperational: boolean,
    hasSupportedAttachments: boolean
):Promise<string[]> {
    const data = {
        FreetextQuestion: freetextQuestion,
        CopilotResponse: copilotResponse,
        IsOperational: isOperational,
        hasSupportedAttachments: hasSupportedAttachments
    };

    const path = "remedium-api/ap-automation/agent/followupQuestions";
    return rest.post(path, data);
}

export async function getInvoiceContext(documentId: number, isOperational: boolean): Promise<IMessage> {
    let url = "remedium-api/ap-automation/agent/invoiceContext";
    if(!isOperational)
        url+="/nonOperational";
    
    url+=`/${documentId}`;

    const response = await remediumApi.get(url);
    return await response.json();
}

function stripNamespace(valueWithNamespace: string) {
    const splitString = valueWithNamespace.split('.');
    return splitString[splitString.length - 1];
}